import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Home, Users, Plus, CalendarDays, FolderKanban, Bell, Search, LayoutGrid, FileText, FileSignature,
  BellRing, Bot, Calculator, Package, UserCog, BarChart3, Settings, History, ShieldCheck, Columns3, LogOut, UserPlus, CalendarPlus, FilePlus2, Hammer, Building2,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { AssistantFab } from "./ai/AssistantFab";
import { api } from "../lib/api";
import CustomerForm from "./CustomerForm";
import AppointmentForm from "./AppointmentForm";
import ProjectForm from "./ProjectForm";
import { ProfileMenu } from "./ProfileMenu";
import { UserAvatar, roleLabel } from "./UserAvatar";

const QuickCtx = createContext(null);
export const useQuick = () => useContext(QuickCtx);

export function navItems(t, can, role, platform) {
  if (role === "crew")
    return [
      { to: "/", icon: Home, label: t("My Day", "Mi día"), id: "home" },
      { to: "/projects", icon: FolderKanban, label: t("My Projects", "Mis proyectos"), id: "projects" },
      { to: "/calendar", icon: CalendarDays, label: t("My Schedule", "Mi agenda"), id: "calendar" },
      { to: "/calculator", icon: Calculator, label: t("Material Calculator", "Calculadora"), id: "calculator" },
    ];
  return [
    { to: "/", icon: Home, label: t("Home", "Inicio"), id: "home" },
    { to: "/customers", icon: Users, label: t("Customers", "Clientes"), id: "customers" },
    { to: "/pipeline", icon: Columns3, label: t("Pipeline", "Embudo"), id: "pipeline" },
    { to: "/calendar", icon: CalendarDays, label: t("Calendar", "Calendario"), id: "calendar" },
    { to: "/estimates", icon: FileText, label: t("Estimates", "Presupuestos"), id: "estimates" },
    { to: "/contracts", icon: FileSignature, label: t("Contracts", "Contratos"), id: "contracts" },
    { to: "/projects", icon: FolderKanban, label: t("Projects", "Proyectos"), id: "projects" },
    { to: "/followups", icon: BellRing, label: t("Follow-ups", "Seguimientos"), id: "followups" },
    { to: "/warranties", icon: ShieldCheck, label: t("Warranties", "Garantías"), id: "warranties" },
    can("ai_assistant") && { to: "/assistant", icon: Bot, label: t("AI Assistant", "Asistente IA"), id: "assistant" },
    { to: "/calculator", icon: Calculator, label: t("Material Calculator", "Calculadora"), id: "calculator" },
    can("inventory") && { to: "/inventory", icon: Package, label: t("Inventory", "Inventario"), id: "inventory" },
    can("reports") && { to: "/reports", icon: BarChart3, label: t("Reports", "Reportes"), id: "reports" },
    (role === "owner" || can("employees")) && { to: "/team", icon: UserCog, label: t("Team & Permissions", "Equipo y permisos"), id: "team" },
    can("activity") && { to: "/activity", icon: History, label: t("Activity Log", "Historial"), id: "activity" },
    platform && { to: "/companies", icon: Building2, label: t("Companies (Platform)", "Empresas (Plataforma)"), id: "companies" },
    { to: "/settings", icon: Settings, label: t("Settings", "Configuración"), id: "settings" },
  ].filter(Boolean);
}

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex rounded-full border border-slate-300 bg-white p-0.5 text-xs font-bold" data-testid="lang-switch">
      {["en", "es"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
          className={`h-8 w-10 rounded-full transition-colors ${lang === l ? "bg-slate-900 text-white" : "text-slate-600"}`}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

function TopBar() {
  const { t } = useLang();
  const { isStaff } = useAuth();
  const [unread, setUnread] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    api.get("/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, [loc.pathname]);
  return (
    <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
      <div className="flex items-center gap-2 h-16 px-4 lg:px-8">
        <Link to="/" className="flex items-center gap-2 lg:hidden min-w-0" data-testid="topbar-home-link">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#D9531E] text-white font-extrabold">HC</span>
          <span className="font-extrabold text-slate-900 truncate text-sm leading-tight">H Calix<br /><span className="font-medium text-slate-500 text-xs">Stucco Design & Paint</span></span>
        </Link>
        <div className="flex-1" />
        {isStaff && (
          <Link to="/customers?focus=1" className="btn-outline btn-sm px-3" data-testid="topbar-search-btn" aria-label="Search">
            <Search className="w-5 h-5" /> <span className="hidden sm:inline">{t("Search customers", "Buscar clientes")}</span>
          </Link>
        )}
        <LangSwitch />
        <Link to="/notifications" className="relative btn-ghost btn-sm px-2" data-testid="topbar-notifications-btn" aria-label="Notifications">
          <Bell className="w-6 h-6" />
          {unread > 0 && <span className="absolute top-1 right-0.5 mono text-[10px] bg-[#D9531E] text-white rounded-full px-1.5" data-testid="notifications-unread-count">{unread}</span>}
        </Link>
        <ProfileMenu />
      </div>
    </header>
  );
}

function Sidebar() {
  const { t } = useLang();
  const { can, user, logout } = useAuth();
  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col bg-[#0F172A] text-slate-300 z-40">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-white/10">
        <span className="grid place-items-center w-10 h-10 rounded-lg bg-[#D9531E] text-white font-extrabold">HC</span>
        <div className="leading-tight"><div className="font-extrabold text-white">H Calix</div><div className="text-xs text-slate-400">Stucco Design & Paint LLC</div></div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
        {navItems(t, can, user.role, user.platform_admin).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`sidebar-${n.id}-link`}
            className={({ isActive }) => `flex items-center gap-3 px-3 h-11 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-[#D9531E] text-white" : "hover:bg-white/5 hover:text-white"}`}>
            <n.icon className="w-5 h-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <Link to="/profile" className="flex items-center gap-3 mb-3 rounded-lg hover:bg-white/5 p-1 -m-1" data-testid="sidebar-profile-link">
          <UserAvatar user={user} testid="sidebar-avatar" />
          <div className="min-w-0">
            <div className="text-sm text-white font-semibold truncate">{user.name}</div>
            <div className="text-xs text-slate-400 truncate">{user.platform_admin ? "Platform Super Admin" : roleLabel(t, user.role)}</div>
          </div>
        </Link>
        <button onClick={logout} className="btn btn-sm w-full bg-white/5 text-slate-200 hover:bg-white/10" data-testid="sidebar-logout-btn">
          <LogOut className="w-4 h-4" /> {t("Log out", "Cerrar sesión")}
        </button>
      </div>
    </aside>
  );
}

const NavIcon = ({ icon: I }) => <I className="w-6 h-6" />;

function BottomNav({ onNew }) {
  const { t } = useLang();
  const { isStaff } = useAuth();
  const cls = ({ isActive }) => `flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-[11px] font-semibold ${isActive ? "text-[#D9531E]" : "text-slate-500"}`;
  const items = isStaff
    ? [["/", Home, t("Home", "Inicio"), "home"], ["/customers", Users, t("Customers", "Clientes"), "customers"], null, ["/calendar", CalendarDays, t("Calendar", "Agenda"), "calendar"], ["/projects", FolderKanban, t("Projects", "Proyectos"), "projects"]]
    : [["/", Home, t("My Day", "Mi día"), "home"], ["/projects", FolderKanban, t("Projects", "Proyectos"), "projects"], ["/calendar", CalendarDays, t("Schedule", "Agenda"), "calendar"], ["/more", LayoutGrid, t("More", "Más"), "more"]];
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 pb-safe" data-testid="bottom-nav">
      <div className="flex items-stretch h-[68px]">
        {items.map((it, i) =>
          it ? (
            <NavLink key={it[0]} to={it[0]} end={it[0] === "/"} className={cls} data-testid={`bottom-nav-${it[3]}`}>
              <NavIcon icon={it[1]} />
              {it[2]}
            </NavLink>
          ) : (
            <div key={i} className="flex-1 flex items-center justify-center">
              <button onClick={onNew} data-testid="bottom-nav-new-btn" aria-label="New"
                className="-mt-7 w-16 h-16 rounded-full bg-[#D9531E] text-white shadow-lg shadow-orange-300/50 grid place-items-center active:scale-95 transition-transform border-4 border-white">
                <Plus className="w-8 h-8" />
              </button>
            </div>
          )
        )}
      </div>
    </nav>
  );
}

function NewSheet({ open, onOpenChange, quick }) {
  const { t } = useLang();
  const { can } = useAuth();
  const nav = useNavigate();
  const go = (fn) => () => { onOpenChange(false); fn(); };
  const items = [
    ["new-customer", UserPlus, t("New Customer", "Nuevo cliente"), () => quick.openCustomer()],
    can("view_prices") && ["new-estimate", FilePlus2, t("New Estimate", "Nuevo presupuesto"), () => nav("/estimates/new")],
    ["new-appointment", CalendarPlus, t("New Appointment", "Nueva cita"), () => quick.openAppt()],
    ["new-project", Hammer, t("New Project", "Nuevo proyecto"), () => quick.openProject()],
  ].filter(Boolean);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl" data-testid="new-action-sheet">
        <SheetHeader><SheetTitle>{t("Create new", "Crear nuevo")}</SheetTitle></SheetHeader>
        <div className="grid grid-cols-2 gap-3 mt-4 pb-4">
          {items.map(([id, Icon, l, fn]) => (
            <button key={id} onClick={go(fn)} data-testid={`sheet-${id}-btn`} className="card flex flex-col items-start gap-3 p-4 min-h-[96px] text-left font-bold hover:border-[#D9531E] active:scale-[0.98] transition-transform">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-orange-50 text-[#D9531E]"><Icon className="w-5 h-5" /></span>
              {l}
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function Layout() {
  const [sheet, setSheet] = useState(false);
  const [dlg, setDlg] = useState(null);
  const nav = useNavigate();
  const quick = {
    openCustomer: (initial, onSaved) => setDlg({ kind: "customer", initial, onSaved: onSaved || ((c) => !initial && nav(`/customers/${c.id}`)) }),
    openAppt: (initial, onSaved) => setDlg({ kind: "appt", initial, onSaved }),
    openProject: (initial, onSaved) => setDlg({ kind: "project", initial, onSaved: onSaved || ((p) => !initial?.id && nav(`/projects/${p.id}`)) }),
    openNew: () => setSheet(true),
  };
  const close = () => setDlg(null);
  return (
    <QuickCtx.Provider value={quick}>
      <Sidebar />
      <div className="lg:pl-[260px] min-h-screen">
        <TopBar />
        <main className="px-4 lg:px-8 py-5 pb-28 lg:pb-10 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
      <BottomNav onNew={() => setSheet(true)} />
      <AssistantFab />
      <NewSheet open={sheet} onOpenChange={setSheet} quick={quick} />
      {dlg?.kind === "customer" && <CustomerForm initial={dlg.initial} onClose={close} onSaved={dlg.onSaved} />}
      {dlg?.kind === "appt" && <AppointmentForm initial={dlg.initial} onClose={close} onSaved={dlg.onSaved} />}
      {dlg?.kind === "project" && <ProjectForm initial={dlg.initial} onClose={close} onSaved={dlg.onSaved} />}
    </QuickCtx.Provider>
  );
}
